'use strict'

import React, { useReducer, useContext } from 'react'
import { mappingInfoToFormData as dateInitFormData } from '../components/DateFormatForm'
import { mappingInfoToFormData as timeInitFormData } from '../components/TimeFormatForm'
import { mappingInfoToFormData as conversionTableInitFormData } from '../components/ConversionTableForm'
import { mappingInfoToFormData as valueMappingInitFormData } from '../components/ValueMappingInputForm'
import { mappingInfoToFormData as valueMappingDndInitFormData } from '../components/ValueMappingDndForm'
import { mappingInfoToFormData as fourArithmeticInitFormData } from '../components/FourArithmeticForm'
import { mappingInfoToFormData as singleConditionInitFormData } from '../components/SingleConditionForm'
import { mappingInfoToFormData as multiConditionInitFormData } from '../components/MultiConditionForm'
import { validateDateFormatForm } from '../components/DateFormatForm'
import { validateTimeFormatForm } from '../components/TimeFormatForm'
import { validateConversionTableForm } from '../components/ConversionTableForm'
import { validateValueMappingForm } from '../components/ValueMappingInputForm'
import { validateValueMappingDndForm } from '../components/ValueMappingDndForm'
import { validateFourArithmeticForm } from '../components/FourArithmeticForm'
import { validateSingleConditionForm } from '../components/SingleConditionForm'
import { validateMultiConditionForm } from '../components/MultiConditionForm'
import { formDataToMappingInfo as dateFormToMappingInfo } from '../components/DateFormatForm'
import { formDataToMappingInfo as timeFormToMappingInfo } from '../components/TimeFormatForm'
import { formDataToMappingInfo as conversionTableFormToMappingInfo } from '../components/ConversionTableForm'
import { formDataToMappingInfo as valueMappingFormToMappingInfo } from '../components/ValueMappingInputForm'
import { formDataToMappingInfo as valueMappingDndFormToMappingInfo } from '../components/ValueMappingDndForm'
import { formDataToMappingInfo as fourArithmeticFormToMappingInfo } from '../components/FourArithmeticForm'
import { formDataToMappingInfo as singleConditionFormToMappingInfo } from '../components/SingleConditionForm'
import { formDataToMappingInfo as multiConditionFormToMappingInfo } from '../components/MultiConditionForm'

const initialState = {
  fieldKey: null,
  colName: '',
  selectedType: '',
  formData: {},
  errors: {},
  previewRows: [],
  /** IF02099 errors[] を表示用に正規化した配列 */
  previewErrors: [],
  /** IF02099 warnings[] を表示用に正規化した配列 */
  previewWarnings: [],
  /** IF02099 API 呼び出し失敗時のエラーメッセージ */
  previewApiError: null,
  previewLoading: false,
  previewRequested: false
}

function getSelectedTypeFromInitialMappingInfo(initialMappingInfo) {
  const mappingType = initialMappingInfo?.MappingType ?? initialMappingInfo?.mappingType ?? ''
  if (mappingType === 'ValueMapping') {
    const valueMappingUiType = initialMappingInfo?._valueMappingUiType
    if (valueMappingUiType === 'manual') return 'value_mapping'
    if (valueMappingUiType === 'dnd' || initialMappingInfo?._isDnd === true) {
      return 'value_mapping_dnd'
    }
  }
  const typeMap = {
    SimpleMapping: 'simple',
    Constant: 'constant',
    ConversionTable: 'conversion_table',
    ValueMapping: 'value_mapping',
    date_format: 'date_format',
    time_format: 'time_format',
    FourArithmeticExpressions: 'four_arithmetic',
    SingleCondition: 'single_condition',
    multi_condition: 'multi_condition'
  }
  return typeMap[mappingType] ?? ''
}

function normalizeValueMappingInitialMappingInfo(initialMappingInfo) {
  const valueMapping =
    initialMappingInfo?.ValueMapping ?? initialMappingInfo?.valueMapping ?? initialMappingInfo?.value_mapping ?? {}
  const mappingList =
    valueMapping?.MappingList ??
    valueMapping?.mappingList ??
    valueMapping?.mapping_list ??
    initialMappingInfo?.MappingList ??
    initialMappingInfo?.mappingList ??
    []
  const list = Array.isArray(mappingList) ? mappingList : []
  return {
    valueMapping: {
      mappingFileType:
        valueMapping?.mappingFileType ?? valueMapping?.MappingFileType ?? valueMapping?.mapping_file_type ?? '',
      mappingColumnName:
        valueMapping?.mappingColumnName ?? valueMapping?.MappingColumnName ?? valueMapping?.mapping_column_name ?? '',
      mappingList: list
    }
  }
}

function getInitialFormData(selectedType, initialMappingInfo, fileTypes) {
  if (!selectedType) return {}
  switch (selectedType) {
    case 'simple': {
      const simpleMapping = initialMappingInfo?.SimpleMapping ?? initialMappingInfo?.simpleMapping ?? {}
      return {
        mappingFileType: simpleMapping?.MappingFileType ?? simpleMapping?.mappingFileType ?? '',
        mappingColumnName: simpleMapping?.MappingColumnName ?? simpleMapping?.mappingColumnName ?? ''
      }
    }
    case 'constant':
      return { constant: initialMappingInfo?.Constant ?? initialMappingInfo?.constant ?? '' }
    case 'conversion_table':
      return conversionTableInitFormData(initialMappingInfo, fileTypes)
    case 'value_mapping':
      return valueMappingInitFormData(normalizeValueMappingInitialMappingInfo(initialMappingInfo), fileTypes)
    case 'value_mapping_dnd':
      return valueMappingDndInitFormData(normalizeValueMappingInitialMappingInfo(initialMappingInfo), fileTypes)
    case 'date_format':
      return dateInitFormData(initialMappingInfo, fileTypes)
    case 'time_format':
      return timeInitFormData(initialMappingInfo, fileTypes)
    case 'four_arithmetic':
      return fourArithmeticInitFormData(initialMappingInfo, fileTypes)
    case 'single_condition':
      return singleConditionInitFormData(initialMappingInfo, fileTypes)
    case 'multi_condition':
      return multiConditionInitFormData(initialMappingInfo, fileTypes)
    default:
      return {}
  }
}

function mappingModalReducer(state, action) {
  switch (action.type) {
    case 'OPEN': {
      const { fieldKey, colName, fileTypes, initialMappingInfo } = action.payload ?? {}
      const selectedType = getSelectedTypeFromInitialMappingInfo(initialMappingInfo)
      return {
        ...initialState,
        fieldKey,
        colName,
        selectedType,
        formData: getInitialFormData(selectedType, initialMappingInfo, fileTypes)
      }
    }
    case 'SET_TYPE': {
      const { type, fileTypes } = action.payload ?? {}
      const initializer = FORM_INITIALIZERS[type]
      let formData = initializer ? initializer(fileTypes) : {}
      // 種別切り替え時はファイル種別・項目名をリセット（他種別の選択を持ち越さない）
      if (
        type &&
        typeof formData === 'object' &&
        formData !== null &&
        ('mappingFileType' in formData || 'mappingColumnName' in formData)
      ) {
        formData = { ...formData, mappingFileType: '', mappingColumnName: '' }
      }
      return {
        ...state,
        selectedType: type ?? '',
        formData,
        errors: {},
        previewRows: [],
        previewErrors: [],
        previewWarnings: [],
        previewApiError: null,
        previewLoading: false,
        previewRequested: false
      }
    }
    case 'SET_FORM_DATA':
      // 設定変更（ファイル種別・項目名・日付/時間形式・値変換ルール等）の時点でプレビューを
      // クリアする。変更後の内容は再度「プレビュー表示」を実行して確認する仕様（バグ#3177）。
      return {
        ...state,
        formData: action.payload?.formData ?? state.formData,
        previewRows: [],
        previewErrors: [],
        previewWarnings: [],
        previewApiError: null,
        previewLoading: false,
        previewRequested: false
      }
    case 'SET_ERRORS':
      return { ...state, errors: action.payload?.errors ?? {} }
    case 'SET_PREVIEW':
      return {
        ...state,
        previewRows: action.payload?.rows ?? [],
        previewErrors: Array.isArray(action.payload?.previewErrors) ? action.payload.previewErrors : [],
        previewWarnings: Array.isArray(action.payload?.previewWarnings) ? action.payload.previewWarnings : [],
        previewApiError: action.payload?.previewApiError ?? null,
        previewLoading: false
      }
    case 'SET_PREVIEW_LOADING':
      return {
        ...state,
        previewLoading: action.payload === true,
        previewRequested: action.payload === true ? true : state.previewRequested,
        previewApiError: action.payload === true ? null : state.previewApiError
      }
    case 'RESET':
      return { ...initialState }
    default:
      return state
  }
}

export const FORM_INITIALIZERS = {
  simple: (fileTypes) => ({
    mappingFileType: fileTypes?.[0]?.mappingFileType ?? '',
    mappingColumnName: ''
  }),
  constant: () => ({ constant: '' }),
  date_format: (fileTypes) => dateInitFormData(null, fileTypes),
  time_format: (fileTypes) => timeInitFormData(null, fileTypes),
  conversion_table: (fileTypes) => conversionTableInitFormData(null, fileTypes),
  value_mapping: (fileTypes) => valueMappingInitFormData(null, fileTypes),
  value_mapping_dnd: (fileTypes) => valueMappingDndInitFormData(null, fileTypes),
  four_arithmetic: (fileTypes) => fourArithmeticInitFormData(null, fileTypes),
  single_condition: (fileTypes) => singleConditionInitFormData(null, fileTypes),
  multi_condition: (fileTypes) => multiConditionInitFormData(null, fileTypes)
}

export const VALIDATION_HANDLERS = {
  simple: (fd) => {
    const mappingFileType = (fd.mappingFileType ?? '').toString().trim()
    const mappingColumnName = (fd.mappingColumnName ?? '').toString().trim()
    if (!mappingFileType || !mappingColumnName) {
      return {
        mappingFileType: mappingFileType ? [] : ['ファイル種別を選択してください'],
        mappingColumnName: mappingColumnName ? [] : ['項目名を選択してください']
      }
    }
    return null
  },
  constant: (fd) => {
    const constant = (fd.constant ?? '').toString().trim()
    return constant ? null : { constant: ['固定値を入力してください'] }
  },
  date_format: validateDateFormatForm,
  time_format: validateTimeFormatForm,
  conversion_table: validateConversionTableForm,
  value_mapping: validateValueMappingForm,
  value_mapping_dnd: validateValueMappingDndForm,
  four_arithmetic: validateFourArithmeticForm,
  single_condition: validateSingleConditionForm,
  multi_condition: validateMultiConditionForm
}

export const MAPPING_INFO_CONVERTERS = {
  simple: (fd) => ({
    MappingType: 'SimpleMapping',
    SimpleMapping: {
      MappingFileType: fd.mappingFileType,
      MappingColumnName: fd.mappingColumnName
    }
  }),
  // 固定値はCSVの値列にそのまま出力されるため、value_mapping・条件分岐と同様に前後の空白（全角含む）をトリムして保存する
  constant: (fd) => ({ MappingType: 'Constant', Constant: (fd.constant ?? '').toString().trim() }),
  date_format: dateFormToMappingInfo,
  time_format: timeFormToMappingInfo,
  conversion_table: conversionTableFormToMappingInfo,
  value_mapping: valueMappingFormToMappingInfo,
  value_mapping_dnd: valueMappingDndFormToMappingInfo,
  four_arithmetic: fourArithmeticFormToMappingInfo,
  single_condition: singleConditionFormToMappingInfo,
  multi_condition: multiConditionFormToMappingInfo
}

export const MappingModalContext = React.createContext(null)
export const MappingModalDispatchContext = React.createContext(null)

export function MappingModalProvider({ children }) {
  const [state, dispatch] = useReducer(mappingModalReducer, initialState)
  return (
    <MappingModalContext.Provider value={state}>
      <MappingModalDispatchContext.Provider value={dispatch}>{children}</MappingModalDispatchContext.Provider>
    </MappingModalContext.Provider>
  )
}

export const useMappingModalState = () => useContext(MappingModalContext)
export const useMappingModalDispatch = () => useContext(MappingModalDispatchContext)
